/**
 * Player movement physics.
 *
 * Values are in native pixels per 1/60s frame, matching the 16-bit games where
 * a tile is 16px. Working in per-frame units (rather than px/sec) is what makes
 * the numbers below match the community-documented originals and keeps the
 * feel stable regardless of display refresh rate — the fixed 60Hz timestep in
 * `core/loop.ts` guarantees one physics step per frame exactly.
 *
 * Reference frame: the reference editor shares one physics model across its
 * visual styles, the model the newer reference platformers use. See
 * docs/research/02-physics.md for sourcing and confidence ratings, and for the
 * derivation of the jump-height figures.
 */

/** Movement tuning for one power state. */
export interface MoveProfile {
  /** Max horizontal speed while walking. */
  walkSpeed: number
  /** Max horizontal speed while running (run button held). */
  runSpeed: number
  /** Acceleration toward `walkSpeed` when the run button is not held. */
  walkAccel: number
  /** Acceleration toward `runSpeed` when the run button is held. */
  runAccel: number
  /** Deceleration when no direction is held. */
  friction: number
  /** Deceleration when turning around against current motion. */
  skidDecel: number
  /**
   * Multiplier on the skid. `applyHorizontal` derives the skid as
   * `2 x selected acceleration x skidScale`, which keeps the verified 2x rule
   * intact and lets a slippery surface cut the skid without redefining what a
   * skid *is*. 1 on every normal surface.
   */
  skidScale: number
  /** Upward velocity applied on the first frame of a jump. */
  jumpVelocity: number
  /** Downward acceleration while ascending and holding jump. */
  gravityRise: number
  /** Downward acceleration once falling. */
  gravityFall: number
  /** Horizontal acceleration while airborne. */
  airAccel: number
  /** Extra air control when the run button is held. */
  airRunAccel: number
}

/** Shared constants across power states. */
export const PHYS = {
  /** Absolute fall speed cap. The reference game's intended cap is 4; its cap check is buggy
   *  and community measurement puts the practical maximum nearer 4.5. */
  maxFall: 4.0,
  /**
   * Below this |vy| the player is "at apex": gravity is scaled down, producing
   * the characteristic hang time.
   *
   * NOTE: the reference game has *no* apex hang at all — verified from the
   * disassembly, gravity is constant at 0.125 for the whole rise. This exists
   * because the reference editor shares the newer platformers' physics, and
   * their decompilation shows a 6-stage gravity ladder with separate
   * button-held/button-released tables (`PlayerJumpGravityData`). The hang is
   * emergent from that ladder, not a special case. See docs/research/02-physics.md.
   */
  apexThreshold: 1.0,
  /** Gravity multiplier while at apex. */
  apexGravityScale: 0.5,
  /** Below this |vy| at apex, gravity is scaled further still. */
  apexFineThreshold: 0.5,
  /** Gravity multiplier at the very top of the arc. */
  apexFineGravityScale: 0.25,
  /**
   * Downward velocity after stomping an enemy. Verified as -4 px/f for most
   * enemies in the reference game, with a lower -3 for a documented few.
   */
  stompBounce: -4.0,
  /** Downward velocity after bouncing off a spring. */
  springBounce: -10.0,
  /**
   * Grace period after walking off a ledge during which a jump still works.
   *
   * DIVERGENCE: this is 0 in the originals — verified from the reference game
   * disassembly, where the jump is rejected unless the player is grounded on
   * the exact frame the button is pressed. Set to 3 as a deliberate
   * quality-of-life choice; it makes levels slightly more forgiving than the
   * originals. Set to 0 for strict fidelity.
   */
  coyoteTime: 3,
  /**
   * How long a jump press is remembered before landing.
   *
   * DIVERGENCE: 0 in the originals, same reasoning as `coyoteTime`. The
   * community jump-buffer patch exists for the reference game precisely because
   * the base game lacks it.
   */
  jumpBuffer: 3,
  /** Frames of squash-and-stretch after landing. */
  landSquashFrames: 6,
  /**
   * Frames the ceiling is ignored after a head bump launches the player.
   * A bump is only detected once the ceiling has already zeroed vertical
   * velocity, so without this window any block that launches Tavi (a note
   * block, say) would be immediately overridden and Tavi would stay pinned
   * underneath it. The originals do the same thing.
   */
  bumpPassThrough: 8,
  /**
   * Takeoff speed at which the jump velocity tier steps up, in px/f.
   * The reference game selects from discrete speed bands, not a curve: below this threshold
   * you get one launch speed, at or above it the next.
   */
  jumpTierSpeed: 1.5625,
  /** Upper launch speed, used at or above `jumpTierSpeed`. */
  jumpVelocityFast: -5.0,
} as const

/**
 * Small Tavi.
 *
 * Speeds and accelerations are the verified reference-game values (see
 * docs/research/02-physics.md for the disassembly sources and unit
 * conversions). One correction to a common assumption: in the reference game the *run*
 * acceleration is **higher** than the walk acceleration (0.0557 vs 0.0371),
 * not lower. Reaching run speed is quicker than reaching walk speed.
 */
export const SMALL_MOVE: MoveProfile = {
  walkSpeed: 1.5,
  runSpeed: 2.5,
  walkAccel: 0.037109375, // $98 / 4096
  runAccel: 0.055664062, // $E4 / 4096
  friction: 0.05078125, // $D0 / 4096
  /**
   * Skid is not an independent constant: it is *exactly 2x* whichever
   * acceleration would otherwise apply. This is why letting go of a direction
   * while holding the opposite one is dramatically faster than simply turning.
   * The value here is the 2x walk figure, used only for display/tuning; the
   * selection happens in `applyHorizontal`.
   */
  skidDecel: 0.07421875, // 2 x $98 / 4096
  skidScale: 1,
  jumpVelocity: -4.0,
  gravityRise: 0.125, // $20 / 256
  gravityFall: 0.4375, // $70 / 256 -- 3.5x rise, the key feel fact
  /**
   * Air acceleration. The base game has no separate air constant at all: it
   * reuses the ground values and only changes the selection rule, with zero drag
   * when no direction is held. The newer reference platformers do have a
   * separate air table, so a distinct value is legitimate here; ours is set
   * marginally below the ground walk figure to keep the "committed to the
   * jump" feel that is the point of reduced air control.
   */
  airAccel: 0.037109375,
  airRunAccel: 0.055664062,
}

/** Big Tavi: identical horizontal speeds to small Tavi (verified in the reference game —
 *  the max-speed table is never indexed by player size), softer apex. */
export const BIG_MOVE: MoveProfile = {
  walkSpeed: 1.5,
  runSpeed: 2.5,
  walkAccel: 0.037109375,
  runAccel: 0.055664062,
  friction: 0.05078125,
  skidDecel: 0.07421875,
  skidScale: 1,
  jumpVelocity: -4.0,
  gravityRise: 0.1171875, // $1E / 256
  gravityFall: 0.4375,
  airAccel: 0.037109375,
  airRunAccel: 0.055664062,
}

/**
 * Ice.
 *
 * The one place the model is a copy rather than a fresh set of numbers, because
 * a slippery surface is a *grip* change and nothing else: identical speed caps,
 * identical acceleration, identical gravity and the same discrete jump tiers,
 * so an ice jump arc is bit-for-bit the same as a dry one. That is what the real
 * games do — ice is about momentum, not about control — and it also means a
 * mistuned ice constant can never quietly break the 4-tile jump.
 *
 * Two numbers change, and only two:
 *
 *  - `friction` drops from 0.05078125 to **0.01171875** px/f², about 4.3x
 *    slipperier. The base game has no ice, so this is *not* a disassembly value:
 *    it is the community ice figure, the same fixed-point table the base game
 *    uses for its normal
 *    `FrictionData[2]` ($D0 / 4096 = 208) with the ice entry ($30 / 4096 = 48)
 *    in place of the normal one. Cited in the ice row of
 *    docs/research/02-physics.md, which is also why the confidence is recorded
 *    there as community rather than verified.
 *  - `skidScale` is **48/208** — deliberately the *same* ratio the friction
 *    change uses, so the pair cannot drift apart. The real games cut the skid on
 *    ice too, and by a matching factor: on ice a turnaround takes seconds
 *    rather than frames, which is the second half of what makes a surface feel
 *    cold. Deriving it from the friction ratio instead of tuning it separately
 *    is what guarantees the two stay consistent.
 */
export const ICE_MOVE: MoveProfile = {
  ...SMALL_MOVE,
  friction: 0.01171875, // $30 / 4096 -- the ice entry
  skidScale: 0.01171875 / SMALL_MOVE.friction, // 48 / 208, the same ratio
}

/**
 * Current physics profile for a power state.
 *
 * `onIce` is resolved per frame by the player from the tiles under its feet,
 * never cached: the whole mechanic is the transition, and a cached flag would
 * leave Tavi skating one frame after he has stepped off the ice.
 */
export function profileFor(power: 'small' | 'big', onIce = false): MoveProfile {
  const base = power === 'big' ? BIG_MOVE : SMALL_MOVE
  if (!onIce) return base
  // Rebuilt from the live power profile rather than returned whole, so big
  // Tavi keeps his own (softer) rise gravity while standing on ice.
  return {
    ...base,
    friction: ICE_MOVE.friction,
    skidScale: ICE_MOVE.skidScale,
  }
}

/** Which way the player wants to move, -1/0/1. */
export interface MoveIntent {
  /** -1 left, +1 right, 0 none. */
  dir: number
  /** Run button held. */
  run: boolean
  /** Jump button state. */
  jump: boolean
  /** True only on the frame jump went down. */
  jumpPressed: boolean
  /** Down button held (duck). */
  down: boolean
}

/** Mutable per-frame player physics state. */
export interface MoveState {
  /** Frames since the last jump press, for buffering. */
  jumpBuffer: number
  /** Frames since leaving the ground without jumping, for coyote time. */
  coyote: number
  /** True while the player is turning against their momentum. */
  skidding: boolean
  /** True while ascending with jump held (variable-height jump in progress). */
  rising: boolean
  /** Frames of landing squash remaining. */
  squash: number
  /** Animation timer for the walk cycle. */
  /** Fractional four-step gait phase; advanced by the player from actual travel. */
  animTimer: number
}

export function newMoveState(): MoveState {
  return {
    jumpBuffer: 0,
    coyote: 0,
    skidding: false,
    rising: false,
    squash: 0,
    animTimer: 0,
  }
}

/**
 * Apply one frame of horizontal movement.
 *
 * The model is: accelerate toward a target speed. The target is the run speed
 * while accelerating, the walk speed once at it, and the sign flips to match
 * the input direction. Pressing against your own motion uses the much larger
 * `skidDecel`, which is what gives Tavi that distinctive hard-to-reverse turn.
 */
export function applyHorizontal(
  state: MoveState,
  vx: number,
  grounded: boolean,
  intent: MoveIntent,
  p: MoveProfile,
): number {
  const { dir, run } = intent

  if (dir === 0) {
    // No input. The base game only applies friction in the air when a direction is
    // held, so an airborne player with no input experiences ZERO drag and
    // keeps all momentum. Momentum preservation is the whole point of the
    // air state, so this is load-bearing rather than an omission.
    if (grounded) {
      const f = p.friction
      if (Math.abs(vx) <= f) return 0
      return vx > 0 ? vx - f : vx + f
    }
    state.skidding = false
    return vx
  }

  // Which acceleration this frame would use. Ground and air differ only in
  // the newer reference platformers (which have a separate air table); the base
  // game reuses the ground values and varies only the selection rule.
  const base = grounded
    ? run
      ? p.runAccel
      : p.walkAccel
    : run
      ? p.airRunAccel
      : p.airAccel

  const maxSpeed = run ? p.runSpeed : p.walkSpeed

  // Turning against current motion => skid.
  const opposing = vx !== 0 && Math.sign(vx) !== Math.sign(dir)
  state.skidding = opposing

  // Skid is exactly 2x the selected acceleration (verified in the reference game), scaled by
  // `skidScale`. This is why a turnaround is so much faster than coasting to a
  // stop, and it is the mechanical basis of the "hold both directions" speed
  // trick. `skidScale` is 1 everywhere except ice, where it is the friction
  // ratio again — see ICE_MOVE.
  if (opposing) {
    const d = base * 2 * p.skidScale
    if (Math.abs(vx) <= d) return 0
    return vx - Math.sign(vx) * d
  }

  const target = dir * maxSpeed
  if (Math.abs(vx) >= maxSpeed) {
    // Above the current cap (e.g. the run button was just released): decay
    // gently into it rather than clamping, so the transition is not a hard stop.
    return vx > 0 ? Math.max(target, vx - base) : Math.min(target, vx + base)
  }
  const next = vx + base * dir
  return Math.abs(next) > maxSpeed ? target : next
}

/**
 * Gravity for the current frame, including the apex-hang modulation that
 * gives the jump its floaty top.
 */
export function gravityFor(
  vy: number,
  rising: boolean,
  p: MoveProfile,
): number {
  let g = rising ? p.gravityRise : p.gravityFall
  const a = Math.abs(vy)
  if (a < PHYS.apexFineThreshold) g *= PHYS.apexFineGravityScale
  else if (a < PHYS.apexThreshold) g *= PHYS.apexGravityScale
  return g
}

/**
 * One frame of vertical movement. Call AFTER horizontal movement and after
 * `onGround` has been resolved for this step.
 *
 * Variable jump height works by using light gravity while ascending with jump
 * held, and switching to full gravity the moment the button is released. That
 * single switch is what gives full control over jump height.
 */
export function applyVertical(
  state: MoveState,
  vy: number,
  intent: MoveIntent,
  p: MoveProfile,
): number {
  if (vy < 0) {
    // Ascending.
    if (!intent.jump) {
      state.rising = false
      // Jump cut: released early, so fall fast.
      return vy + p.gravityFall * 2
    }
    state.rising = true
    return vy + gravityFor(vy, true, p)
  }
  return vy + gravityFor(vy, false, p)
}

/**
 * Velocity for a jump started at the given horizontal speed.
 *
 * This is a **discrete step, not a curve** — verified from the reference game
 * disassembly, which selects a launch-velocity tier from `Player_XSpeedAbsolute`
 * against fixed thresholds. Below 1.5625 px/f the launch speed is -4; at or
 * above it, -5. Multiple independent sources confirm the discreteness, and it
 * is a large part of why the games feel the way they do: there is a crisp,
 * learnable "walk jump" versus "run jump" distinction, and a player learns to
 * commit to one or the other rather than modulating a continuum.
 *
 * A smooth interpolation between the two speeds was tried first and felt
 * mushy — there was no decision point for the player to learn.
 *
 * With the verified gravities this yields a ~4-tile standing jump and a ~6-tile
 * running jump, matching the derived figures in docs/research/02-physics.md.
 */
export function jumpVelocityFor(vx: number, p: MoveProfile): number {
  if (Math.abs(vx) >= PHYS.jumpTierSpeed) return PHYS.jumpVelocityFast
  return p.jumpVelocity
}

/** Clamp to the terminal velocity. */
export function clampFall(vy: number): number {
  return vy > PHYS.maxFall ? PHYS.maxFall : vy
}
